import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Settings as SettingsIcon,
  KeyRound,
  Sparkles,
  Wrench,
  CheckCircle2,
  XCircle,
  Loader2,
  PlugZap,
} from "lucide-react";
import {
  adminGetAiConfig,
  adminGetMaintenance,
  adminListSecretStatus,
  adminSaveAiConfig,
  adminSaveMaintenance,
  adminTestService,
} from "@/lib/admin-platform.functions";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  component: SettingsPage,
});

type Service = "twelve_data" | "fmp" | "news_api" | "fred" | "anthropic" | "telegram";

const SERVICE_LABELS: Record<Service, string> = {
  twelve_data: "Twelve Data",
  fmp: "Financial Modeling Prep",
  news_api: "NewsAPI",
  fred: "FRED",
  anthropic: "Anthropic AI",
  telegram: "Telegram Bot",
};

function SettingsPage() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="rounded-md border border-primary/40 bg-primary/10 p-1.5 text-primary">
          <SettingsIcon className="h-4 w-4" />
        </div>
        <h2 className="text-lg font-bold tracking-tight">Platform Settings</h2>
      </div>

      <Tabs defaultValue="keys" className="space-y-4">
        <TabsList>
          <TabsTrigger value="keys">API Keys</TabsTrigger>
          <TabsTrigger value="ai">AI Engine</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
        </TabsList>

        <TabsContent value="keys">
          <ApiKeysPanel />
        </TabsContent>
        <TabsContent value="ai">
          <AiConfigPanel />
        </TabsContent>
        <TabsContent value="maintenance">
          <MaintenancePanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ApiKeysPanel() {
  const listFn = useServerFn(adminListSecretStatus);
  const testFn = useServerFn(adminTestService);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "platform", "secrets"],
    queryFn: () => listFn(),
  });

  const [testing, setTesting] = useState<Service | null>(null);
  const [results, setResults] = useState<
    Record<string, { ok: boolean; status?: number; latencyMs: number; error?: string }>
  >({});

  const runTest = async (svc: Service) => {
    setTesting(svc);
    try {
      const r = await testFn({ data: { service: svc } });
      setResults((p) => ({ ...p, [svc]: r }));
      if (r.ok) toast.success(`${SERVICE_LABELS[svc]} connection OK`);
      else toast.error(`${SERVICE_LABELS[svc]}: ${r.error ?? `HTTP ${r.status}`}`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-border/60 bg-card/40 p-5">
        <div className="mb-3 flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-bold">Secret status</h3>
          <span className="ml-auto text-xs text-muted-foreground">
            Values are masked. Manage in Lovable Cloud → Secrets.
          </span>
        </div>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {(data?.keys ?? []).map((k) => (
              <div
                key={k.name}
                className="flex items-center justify-between rounded-md border border-border/60 bg-background/40 p-3"
              >
                <div className="min-w-0">
                  <div className="font-mono text-xs">{k.name}</div>
                  <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                    {k.set ? k.masked : "not set"}
                  </div>
                </div>
                <Badge variant={k.set ? "outline" : "destructive"} className="text-[10px]">
                  {k.set ? `${k.length} chars` : "missing"}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="border-border/60 bg-card/40 p-5">
        <div className="mb-3 flex items-center gap-2">
          <PlugZap className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-bold">Test connections</h3>
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {(Object.keys(SERVICE_LABELS) as Service[]).map((svc) => {
            const r = results[svc];
            return (
              <div
                key={svc}
                className="flex items-center justify-between rounded-md border border-border/60 bg-background/40 p-3"
              >
                <div className="flex items-center gap-2">
                  {r ? (
                    r.ok ? (
                      <CheckCircle2 className="h-4 w-4 text-success" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )
                  ) : (
                    <div className="h-2 w-2 rounded-full bg-muted-foreground/40" />
                  )}
                  <div>
                    <div className="text-sm font-medium">{SERVICE_LABELS[svc]}</div>
                    {r && (
                      <div className="text-[10px] text-muted-foreground">
                        {r.ok
                          ? `OK · ${r.latencyMs}ms`
                          : r.error ?? `HTTP ${r.status}`}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runTest(svc)}
                  disabled={testing === svc}
                >
                  {testing === svc ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    "Test"
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function AiConfigPanel() {
  const qc = useQueryClient();
  const loadFn = useServerFn(adminGetAiConfig);
  const saveFn = useServerFn(adminSaveAiConfig);

  const { data } = useQuery({
    queryKey: ["admin", "platform", "ai"],
    queryFn: () => loadFn(),
  });

  const [model, setModel] = useState<string>("");
  const [maxTokens, setMaxTokens] = useState<number>(1024);
  const [refresh, setRefresh] = useState<number>(60);
  const [hydrated, setHydrated] = useState(false);

  if (data && !hydrated) {
    setModel(data.config.anthropic_model);
    setMaxTokens(data.config.max_tokens);
    setRefresh(data.config.agent_refresh_seconds);
    setHydrated(true);
  }

  const saveMut = useMutation({
    mutationFn: () =>
      saveFn({
        data: {
          anthropic_model: model as never,
          max_tokens: maxTokens,
          agent_refresh_seconds: refresh,
        },
      }),
    onSuccess: () => {
      toast.success("AI config saved");
      qc.invalidateQueries({ queryKey: ["admin", "platform", "ai"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="border-border/60 bg-card/40 p-5">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-bold">Anthropic engine</h3>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <Label className="text-xs">Model</Label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger>
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent>
              {(data?.models ?? []).map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Max tokens</Label>
          <Input
            type="number"
            min={64}
            max={8192}
            value={maxTokens}
            onChange={(e) => setMaxTokens(Number(e.target.value))}
          />
        </div>
        <div>
          <Label className="text-xs">Agent refresh (seconds)</Label>
          <Input
            type="number"
            min={15}
            max={3600}
            value={refresh}
            onChange={(e) => setRefresh(Number(e.target.value))}
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !model}>
          {saveMut.isPending ? "Saving…" : "Save AI config"}
        </Button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Consumed by the agent council on each signal generation. Lower refresh = faster but more API
        cost.
      </p>
    </Card>
  );
}

function MaintenancePanel() {
  const qc = useQueryClient();
  const loadFn = useServerFn(adminGetMaintenance);
  const saveFn = useServerFn(adminSaveMaintenance);

  const { data } = useQuery({
    queryKey: ["admin", "platform", "maintenance"],
    queryFn: () => loadFn(),
  });

  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [hydrated, setHydrated] = useState(false);

  if (data && !hydrated) {
    setEnabled(data.maintenance.enabled);
    setMessage(data.maintenance.message);
    setHydrated(true);
  }

  const saveMut = useMutation({
    mutationFn: () => saveFn({ data: { enabled, message } }),
    onSuccess: () => {
      toast.success("Maintenance settings saved");
      qc.invalidateQueries({ queryKey: ["admin", "platform", "maintenance"] });
      qc.invalidateQueries({ queryKey: ["maintenance-public"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="border-border/60 bg-card/40 p-5">
      <div className="mb-4 flex items-center gap-2">
        <Wrench className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-bold">Maintenance mode</h3>
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-md border border-border/60 bg-background/40 p-4">
          <div>
            <div className="text-sm font-medium">Enable maintenance mode</div>
            <div className="text-xs text-muted-foreground">
              Visitors see a maintenance page. Admins remain unaffected.
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>
        <div>
          <Label className="text-xs">Maintenance message</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="mt-1 min-h-[100px]"
            maxLength={500}
          />
          <p className="mt-1 text-[10px] text-muted-foreground">{message.length}/500</p>
        </div>
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || message.length === 0}
          >
            {saveMut.isPending ? "Saving…" : "Save maintenance settings"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
